import {
  extractAxiosErrorMessage,
  HealthEvent,
  SolarEdgeAlertAcknowledgement,
  useSolarEdgeAlertAcknowledgement,
} from "@inrange/building-manager-api-client";
import { siteLocalDatetimeValueToUtcIso } from "@inrange/theme-components";
import { useState } from "react";
import { Button, Card, Form, Modal, Table } from "react-bootstrap";
import { formatSiteDate, formatSiteDateTime } from "./healthDateFilters";
import stringToReactNodesWithLinks from "./siteSections/stringToReactNodesWithLinks";

// One entry of `details.alerts` as written by BMA's solaredge_alert check (snake_case).
type SolarEdgeAlert = {
  alert_id?: number;
  type?: string;
  impact?: number;
  component_name?: string;
  component_serial?: string;
  error_codes?: string[];
};

const alertList = (raw: unknown): SolarEdgeAlert[] =>
  Array.isArray(raw) ? (raw as SolarEdgeAlert[]) : [];

// The review date input is a site-local calendar date.
const toSiteDateValue = (iso: string, timeZone: string): string =>
  new Intl.DateTimeFormat("en-CA", { timeZone }).format(new Date(iso));

const describeSolarEdgeAlert = (alert: SolarEdgeAlert): string => {
  const component = [alert.component_name, alert.component_serial]
    .filter((v) => v != null && v !== "")
    .join(" ");
  const errorCodes = Array.isArray(alert.error_codes) ? alert.error_codes : [];
  return [
    alert.type,
    alert.impact != null && `impact ${alert.impact}`,
    component && `on ${component}`,
    errorCodes.length > 0 && `error codes ${errorCodes.join(", ")}`,
  ]
    .filter(Boolean)
    .join(" · ");
};

const AcknowledgeSolarEdgeAlertModal = ({
  siteId,
  alert,
  timeZone,
  onHide,
  existing,
}: {
  siteId: string;
  alert: SolarEdgeAlert;
  timeZone: string;
  onHide: () => void;
  existing?: SolarEdgeAlertAcknowledgement;
}) => {
  const { acknowledge } = useSolarEdgeAlertAcknowledgement();
  const [note, setNote] = useState(existing?.note ?? "");
  const [reviewDate, setReviewDate] = useState(
    existing?.reviewAt ? toSiteDateValue(existing.reviewAt, timeZone) : ""
  );

  const submit = () => {
    acknowledge.mutate(
      {
        siteId,
        alertId: alert.alert_id!,
        note: note.trim(),
        reviewAt: reviewDate
          ? siteLocalDatetimeValueToUtcIso(`${reviewDate}T00:00`, timeZone)
          : null,
      },
      {
        onSuccess: onHide,
        onError: async (err) =>
          window.alert(
            `Failed to save acknowledgement: ${await extractAxiosErrorMessage(err)}`
          ),
      }
    );
  };

  return (
    <Modal show onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>
          {existing ? "Edit acknowledgement" : "Acknowledge SolarEdge alert"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="small">{describeSolarEdgeAlert(alert)}</p>
        <p className="small text-muted">
          The alert stops raising an open health issue (digest, current health
          tables and counts) until the review date passes, SolarEdge closes it,
          or the acknowledgement is removed. A new alert, even of the same type,
          still alerts.
        </p>
        <Form.Group className="mb-3">
          <Form.Label>Why are we living with this alert?</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>Review date (optional)</Form.Label>
          <Form.Control
            type="date"
            value={reviewDate}
            min={new Date(Date.now() + 86_400_000).toISOString().slice(0, 10)}
            onChange={(e) => setReviewDate(e.target.value)}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={!note.trim() || acknowledge.isLoading}
          onClick={submit}
        >
          {existing ? "Save" : "Acknowledge"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export const SolarEdgeAlertDetails = ({
  event,
  timeZone,
  canAcknowledge,
}: {
  event: HealthEvent;
  timeZone: string;
  canAcknowledge: boolean;
}) => {
  const [acknowledging, setAcknowledging] = useState<SolarEdgeAlert | null>(
    null
  );
  const alerts = alertList(event.details?.alerts);
  const acknowledged = alertList(event.details?.acknowledged_alerts);
  const past = alertList(event.details?.past_alerts);
  if (alerts.length + acknowledged.length + past.length === 0) return null;
  return (
    <div className="text-muted" style={{ fontSize: "0.75rem" }}>
      {alerts.map((alert, i) => (
        <div key={alert.alert_id ?? i}>
          {describeSolarEdgeAlert(alert)}
          {canAcknowledge && alert.alert_id != null && (
            <Button
              variant="link"
              size="sm"
              className="p-0 ms-2 align-baseline"
              style={{ fontSize: "0.75rem" }}
              onClick={() => setAcknowledging(alert)}
            >
              Acknowledge
            </Button>
          )}
        </div>
      ))}
      {acknowledged.map((alert, i) => (
        <div key={alert.alert_id ?? i}>
          {describeSolarEdgeAlert(alert)} (acknowledged)
        </div>
      ))}
      {past.map((alert, i) => (
        <div key={alert.alert_id ?? i}>
          {describeSolarEdgeAlert(alert)} (no longer open)
        </div>
      ))}
      {acknowledging && (
        <AcknowledgeSolarEdgeAlertModal
          siteId={event.siteId}
          alert={acknowledging}
          timeZone={timeZone}
          onHide={() => setAcknowledging(null)}
        />
      )}
    </div>
  );
};

export const AcknowledgedSolarEdgeAlertsCard = ({
  siteId,
  acknowledgements,
  timeZone,
}: {
  siteId: string;
  acknowledgements: SolarEdgeAlertAcknowledgement[];
  timeZone: string;
}) => {
  const { unacknowledge } = useSolarEdgeAlertAcknowledgement();
  const [editing, setEditing] = useState<SolarEdgeAlertAcknowledgement | null>(
    null
  );
  if (acknowledgements.length === 0) return null;
  return (
    <Card className="mb-3">
      <Card.Header>Acknowledged SolarEdge alerts</Card.Header>
      <Card.Body className="p-0">
        <Table size="sm" className="mb-0 small">
          <thead>
            <tr>
              <th>Alert</th>
              <th>Why we&apos;re living with it</th>
              <th>Acknowledged</th>
              <th>Review</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {acknowledgements.map((ack) => (
              <tr key={ack.alertId}>
                <td>{describeSolarEdgeAlert(ack.alert as SolarEdgeAlert)}</td>
                <td>{stringToReactNodesWithLinks(ack.note)}</td>
                <td>
                  {formatSiteDateTime(ack.acknowledgedAt, timeZone)} by{" "}
                  {ack.acknowledgedBy}
                </td>
                <td>
                  {ack.reviewAt ? formatSiteDate(ack.reviewAt, timeZone) : "-"}
                </td>
                <td>
                  <div className="d-flex gap-1">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      style={{ whiteSpace: "nowrap", fontSize: "0.75rem" }}
                      onClick={() => setEditing(ack)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      style={{ whiteSpace: "nowrap", fontSize: "0.75rem" }}
                      disabled={unacknowledge.isLoading}
                      title="The alert goes back on the open health event"
                      onClick={() =>
                        unacknowledge.mutate(
                          { siteId, alertId: ack.alertId },
                          {
                            onError: async (err) =>
                              window.alert(
                                `Failed to remove acknowledgement: ${await extractAxiosErrorMessage(err)}`
                              ),
                          }
                        )
                      }
                    >
                      Un-acknowledge
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        {editing && (
          <AcknowledgeSolarEdgeAlertModal
            siteId={siteId}
            alert={editing.alert as SolarEdgeAlert}
            timeZone={timeZone}
            existing={editing}
            onHide={() => setEditing(null)}
          />
        )}
      </Card.Body>
    </Card>
  );
};
